import {AfterContentInit, Component, EventEmitter, Input, Output, ViewChild} from '@angular/core';
import '../../../../../utils/window/window-prototype.utils';
import {UserAuthenticationService} from '../../../../user/login/services';
import {ImageContentForm} from '../../../image/form/image-content.form';
import {ImageCardButtons} from '../../interfaces/image-card-buttons.interface';
import {ImageFormService} from '../../services/image-form/image-form-service';
import {CaptionInput} from '../inputs/caption';

/**
 * Component for the image card
 * @implements {AfterContentInit}
 * @class
 *
 * @description Handles the form and save capabilities of image cards
 *
 * # Inputs
 * |name|type|description|
 * |---|---|---|
 * |showImage|boolean|To show the image thumbnail in the card or not|
 * |showCropGravity|boolean|To show the crop gravity field in the card or not|
 * |showTitleAsterisks|boolean|To show the required field asterisks or not|
 * |buttons|[[ImageCardButtons]][]|List of buttons on the image card|
 * |imageContentForm|[[ImageContentForm]]|The image form for this card|
 * |cropGravityChoices|any|Possible crop gravity choices|
 * |imagePath|string|The url of this card's image|
 *
 * # Outputs
 * |name|type|description|
 * |---|---|---|
 * |onSave|EventEmitter|Emitted when this image card is saved|
 */
@Component({
  selector: 'advtech-image-card',
  templateUrl: './image-card.component.html',
})
export class ImageCardComponent implements AfterContentInit {

  /**
   * To show the image thumbnail or not
   * @type {boolean}
   */
  @Input()
  showImage: boolean = true;
  /**
   * To show the crop gravity field or not
   * @type {boolean}
   */
  @Input()
  showCropGravity: boolean = true;

  /**
   * To show the asterisk indicating a primary field or not
   * @type {boolean}
   */
  @Input()
  showTitleAsterisk: boolean = true;

  /**
   * List of buttons on this page
   * @type {ImageCardButtons[]}
   */
  @Input()
  buttons: ImageCardButtons[] = [];

  /**
   * The caption input component
   * @type {CaptionInput}
   */
  @ViewChild(CaptionInput)
  captionInput: CaptionInput;

  /**
   * The image form for this image card
   * @type {ImageContentForm}
   */
  @Input()
  form: ImageContentForm;

  /**
   * Array of crop gravity choices returned from the server
   * @type {any}
   */
  @Input()
  cropGravityChoices: any;

  /**
   * URL path of this image
   * @type {string}
   */
  @Input()
  imagePath: string;

  /**
   * Event emitter for on save of an image card
   * @type {EventEmitter<object>}
   */
  @Output()
  onSave: EventEmitter<object> = new EventEmitter();

  /**
   * The default crop gravity in case one isn't selected
   * @type {string}
   */
  cropGravityModel: string = "";

  /**
   * ID of this image card, so that we can access its image
   * @type {string}
   */
  id: string;

  /**
   * The default credit when the image card appears
   * @type {string}
   */
  creditModel: string;

  /**
   * @param {ImageFormService} imageFormService The ImageFormService instance
   * @param {UserAuthenticationService} userAuthService The UserAuthenticationService instance
   * @constructor
   */
  constructor(public imageFormService: ImageFormService,
              public userAuthService: UserAuthenticationService) {
    this.creditModel = localStorage.getItem('displayName');
    if (!this.creditModel) {
      this.userAuthService.getUser().subscribe((userData) => {
        localStorage.setItem('displayName', userData.username);
        this.creditModel = localStorage.getItem('displayName');
      });
    }
  }

  /**
   * Lifecycle hook for the AfterContentInit interface
   * @returns {void}
   */
  ngAfterContentInit(): void {
    this.cropGravityModel = this.form.abstractEntity.getCropGravity().getValue() || this.cropGravityModel;
    this.id = this.form.getEntity().getId();
  }

  /**
   * Saves the image card
   * @returns {void}
   */
  save(): void {
    this.id = this.form.getEntity().getId();
    this.imageFormService.sendCompletedImage(this.form.getValue(), this.id).subscribe((data) => {
      if (data.status === 'success') {
        this.onSave.emit({id: this.id, data});
      }
    });
  }

  /**
   * Update the quill editor with new data
   * @param {string} caption The caption form data from the ApplyToAllModal
   * @returns {void}
   */
  updateQuill(caption: string): void {
    this.captionInput.setValue(caption);
  }

  /**
   * Clean up this image card's toolbar
   * @returns {void}
   */
  cleanup(): void {
    const querySelector = this.captionInput.quillEditorElement.nativeElement.querySelector('.ql-editor');
    querySelector.off('focusin.quillIosSafari');
    querySelector.off('focusout.quillIosSafari');
  }
}



// WEBPACK FOOTER //
// ./src/app/content/shared/components/image-card/image-card.component.ts